import { useEffect, useState } from 'react';

const preloadMap = new Map<string, boolean>();

/**
 * Replace all '\' with ''
 */
function sanitizeSrcUrl(url?: string) {
  if (!url) return undefined;
  return url.replace(/\\/g, '');
}

/**
 * A React hook that returns
 * true as soon as the image has been preloaded.
 * If an element is defined, the image
 * will be loaded as soon as the element is in
 * the viewport.
 */
export function usePreloadImage(srcParam: string | undefined, shouldObserveElement?: boolean) {
  const [observeElement, setImageElement] = useState<HTMLElement | null>(null);
  const [preloaded, setPreloaded] = useState(false);
  const [imgElem, setImgElem] = useState<HTMLImageElement | null>(null);

  const src = sanitizeSrcUrl(srcParam);

  function onPreloaded() {
    if (!src) return;
    setPreloaded(true);
    preloadMap.set(src, true);
  }

  useEffect(() => {
    if (!src) return;

    let didCancel = false;

    let observer: IntersectionObserver | undefined;

    function preload() {
      if (!src) return;

      if (observer) {
        observer.disconnect();
      }

      const img = new Image();
      img.src = src;
      img.onload = () => {
        if (!didCancel) {
          onPreloaded();
          setImgElem(img);
        }
      };

      // Check if image has already been loaded
      if (img.complete) {
        img.naturalHeight;
        onPreloaded();
      }
    }

    if (shouldObserveElement) {
      if (observeElement) {
        observer = new IntersectionObserver(
          function (entries) {
            if (entries[0].isIntersecting === true) {
              preload();
            }
          },
          { threshold: 0.1 }
        );
        observer.observe(observeElement);
      }
    } else {
      preload();
    }

    return () => {
      didCancel = true;
    };
  }, [src, observeElement]);

  const isPreloaded = (src && preloadMap.has(src)) || preloaded;

  return {
    loadedImageSrc: isPreloaded ? src : undefined,
    setImageElement,
    imgElem,
  };
}
